import { Grid, Tooltip, Typography, makeStyles } from '@material-ui/core';
import { ReportProblemOutlined } from '@material-ui/icons';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Button, CircularLoader, ConfirmDialog, InformationDialog } from '@roc/ui';
import {
  areActionsUnavailableBasedOnDocument,
  CustomTaskDialog,
  TaskStatus,
  useGeneralTasksStore,
} from '@roc/feature-general-tasks';
import { ChatRoomType } from '@roc/feature-communication';
import { DocumentName, useDocumentStore, WithDocumentActions } from '@roc/feature-documents';
import { Document } from '@roc/feature-types';
import { LoanProcess } from '@roc/feature-utils';
import { PostQcReviewStore, QcFilter } from './postQcReviewStore';
import { PostQcFieldTable } from './postQcFieldPanel';
import { PostQcDocumentViewer } from './postQcDocumentViewer';
import { qcPalette } from './postQcPalette';

type ConfirmAction = 'CLOSE' | 'SEND_REVISION' | 'COMPLETE' | null;

const normalizeStatus = (status: string): string => TaskStatus[status] ?? status;

const FULL_VIEWPORT_PAPER = {
  margin: 0,
  width: '100vw',
  maxWidth: '100vw',
  height: '100vh',
  maxHeight: '100vh',
  borderRadius: 0,
  overflow: 'hidden',
};

const REVIEW_VERTICAL_PADDING = 10;

const REVIEW_BOTTOM_GAP = 16 + REVIEW_VERTICAL_PADDING;

const useAvailableHeight = (open: boolean) => {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>(null);

  useLayoutEffect(() => {
    if (!open) {
      return;
    }
    const measure = () => {
      const element = ref.current;
      if (!element) {
        return;
      }
      const actions = element.closest('.MuiDialog-paper')?.querySelector('.MuiDialogActions-root') as HTMLElement;
      const top = element.getBoundingClientRect().top;
      const next = Math.floor(window.innerHeight - top - (actions?.offsetHeight ?? 0) - REVIEW_BOTTOM_GAP);
      setHeight(current => (current === next ? current : Math.max(320, next)));
    };
    const frame = window.requestAnimationFrame(measure);
    window.addEventListener('resize', measure);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', measure);
    };
  });

  return { ref, height };
};

const FILTERS: { value: QcFilter; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'NEEDS_REVISION', label: 'Flagged' },
];

const SHORTCUTS: { keys: string; label: string }[] = [
  { keys: '↑↓', label: 'move' },
  { keys: 'F', label: 'flag / unflag' },
];

const useStyles = makeStyles(theme => ({
  reviewItem: {
    padding: `${REVIEW_VERTICAL_PADDING}px 0 !important`,
  },
  review: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 300px)',
    minHeight: 320,
    borderTop: `1px solid ${qcPalette.border}`,
    borderBottom: `1px solid ${qcPalette.border}`,
    overflow: 'hidden',
    backgroundColor: theme.palette.common.white,
  },
  context: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
    fontSize: 13,
    color: qcPalette.muted,
  },
  loan: {
    fontWeight: 600,
    color: qcPalette.ink,
  },
  statusChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    height: 24,
    padding: '0 10px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 600,
    backgroundColor: qcPalette.verifiedSoft,
    color: '#1B3F9E',
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: qcPalette.verified,
  },
  statusChipRevision: {
    backgroundColor: qcPalette.flaggedSoft,
    color: qcPalette.flaggedInk,
  },
  statusDotRevision: {
    backgroundColor: qcPalette.flagged,
  },
  progress: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(3),
    padding: theme.spacing(1.25, 3),
    backgroundColor: qcPalette.subtle,
    borderBottom: `1px solid ${qcPalette.border}`,
  },
  progressText: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    flex: 1,
    minWidth: 0,
  },
  legendItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  },
  swatch: {
    width: 10,
    height: 10,
    borderRadius: 2,
  },
  mismatchIcon: {
    fontSize: 14,
    color: qcPalette.mismatchInk,
  },
  filters: {
    padding: 4,
    gap: 2,
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${qcPalette.border}`,
    borderRadius: 12,
  },
  filter: {
    height: 36,
    padding: '0 14px',
    border: 'none',
    borderRadius: '9px !important',
    textTransform: 'none',
    fontSize: 13,
    fontWeight: 600,
    color: qcPalette.inkSoft,
    '&.Mui-selected, &.Mui-selected:hover': {
      backgroundColor: qcPalette.navy,
      color: theme.palette.common.white,
    },
    '&.Mui-selected $filterCount': {
      backgroundColor: 'rgba(255,255,255,0.18)',
      color: theme.palette.common.white,
    },
  },
  filterCount: {
    minWidth: 22,
    height: 20,
    marginLeft: 8,
    padding: '0 6px',
    boxSizing: 'border-box',
    borderRadius: 10,
    fontSize: 11,
    fontVariantNumeric: 'tabular-nums',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: qcPalette.sunken,
    color: qcPalette.inkSoft,
  },
  notice: {
    padding: theme.spacing(0.5, 3),
    fontSize: 12,
    backgroundColor: qcPalette.verifiedSoft,
    color: '#1B3F9E',
    borderBottom: `1px solid ${qcPalette.border}`,
  },
  workspace: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
  },
  fields: {
    flex: 1,
    minWidth: 0,
  },
  viewer: {
    width: '55%',
    minWidth: 380,
    flexShrink: 0,
    borderLeft: `1px solid ${qcPalette.border}`,
  },
  state: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    height: 'calc(100vh - 300px)',
    minHeight: 320,
    color: qcPalette.muted,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    width: '100%',
  },
  saveState: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 13,
    color: qcPalette.muted,
    whiteSpace: 'nowrap',
  },
  dirtyDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: qcPalette.flagged,
  },
  shortcuts: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 6,
    flex: 1,
    fontSize: 12,
    color: qcPalette.muted,
  },
  shortcut: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    marginRight: 8,
  },
  kbd: {
    minWidth: 22,
    height: 22,
    padding: '0 6px',
    boxSizing: 'border-box',
    border: `1px solid ${qcPalette.borderStrong}`,
    borderBottomWidth: 2,
    borderRadius: 5,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'inherit',
    fontSize: 11,
    color: qcPalette.ink,
  },
  revisionButton: {
    borderColor: qcPalette.flaggedLine,
    backgroundColor: qcPalette.flaggedSoft,
    color: qcPalette.flaggedInk,
    '&:hover': {
      backgroundColor: '#FFE6D6',
    },
  },
  revisionCount: {
    minWidth: 20,
    height: 20,
    marginLeft: 8,
    borderRadius: 10,
    backgroundColor: qcPalette.flagged,
    color: theme.palette.common.white,
    fontSize: 11,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

const formatTime = (date: Date) => date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

export type PostQcReviewDialogProps = {
  open: boolean;
  onClose: () => void;
  loanId: number | string;
  loanTask: Document;
};

export const PostQcReviewDialog = observer(({ open, onClose, loanId, loanTask }: PostQcReviewDialogProps) => {
  const classes = useStyles();
  const { generalTasksStore } = useGeneralTasksStore();
  const { documentStore } = useDocumentStore();
  const { globalStore } = generalTasksStore;
  const [store] = useState(() => new PostQcReviewStore(globalStore));
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [completeBlocked, setCompleteBlocked] = useState(false);
  const available = useAvailableHeight(open);
  const heightStyle = available.height ? { height: available.height } : undefined;
  const taskId = loanTask?.taskId;
  const taskStatus = normalizeStatus(loanTask?.taskStatus ?? loanTask?.status);
  const accepted = taskStatus === TaskStatus.ACCEPTED;
  const readOnly = !!globalStore.userFeatures?.isClosingAttorney
    || !!loanTask?.readOnly
    || accepted
    || (!!loanTask && areActionsUnavailableBasedOnDocument(loanTask));
  const { counts } = store;
  const filterCounts: Record<QcFilter, number> = {
    ALL: counts.total,
    NEEDS_REVISION: counts.flagged,
  };

  useEffect(() => {
    if (open && loanId) {
      store.load(Number(loanId), taskStatus);
    }
    if (!open) {
      store.reset();
    }
  }, [open, loanId]);

  const close = () => {
    store.reset();
    onClose();
  };

  const closeAndRefreshTodos = () => {
    close();
    if (loanTask?.loanProcess) {
      documentStore?.loanTodosStore?.fetchTodos(Number(loanId), loanTask.loanProcess as LoanProcess);
    }
  };

  const requestClose = () => {
    if (store.isDirty && !readOnly) {
      setConfirmAction('CLOSE');
    } else {
      close();
    }
  };

  const save = async () => {
    if (await store.save()) {
      globalStore.notificationStore.showSuccessNotification({ message: 'Review data saved' });
    }
  };

  const confirm = async () => {
    const action = confirmAction;
    setConfirmAction(null);
    if (action === 'CLOSE') {
      close();
    }
    if (action === 'SEND_REVISION' && (await store.sendRevision())) {
      closeAndRefreshTodos();
    }
    if (action === 'COMPLETE' && counts.flagged === 0 && (await store.save())) {
      generalTasksStore.updateTaskStatusV2(taskId, TaskStatus.ACCEPTED, closeAndRefreshTodos);
    }
  };

  const confirmBody = () => {
    if (confirmAction === 'CLOSE') {
      return 'You have unsaved changes. Close without saving?';
    }
    if (confirmAction === 'SEND_REVISION') {
      return `A PDF listing ${counts.flagged} flagged ${counts.flagged === 1 ? 'field' : 'fields'} will be posted to the loan's Closing chat room and the task will move to More Info Needed.`;
    }
    return <Typography>Please confirm you want to mark the task as completed.</Typography>;
  };

  const sendRevisionHint = counts.flagged === 0 ? 'Flag at least one field to send a revision' : '';

  const renderReview = () => {
    if (store.loading) {
      return (
        <div className={classes.state} ref={available.ref} style={heightStyle}>
          <CircularLoader position="absolute" />
        </div>
      );
    }
    if (store.loadError) {
      return (
        <div className={classes.state} ref={available.ref} style={heightStyle}>
          <Typography>Review data couldn't be loaded.</Typography>
          <Button testId="post-qc-retry" variant="outlined" color="primary" onClick={() => store.load(Number(loanId), taskStatus)}>
            Retry
          </Button>
        </div>
      );
    }
    if (store.sections.length === 0) {
      return (
        <div className={classes.state} ref={available.ref} style={heightStyle}>
          <Typography>There is nothing to review on this loan yet.</Typography>
        </div>
      );
    }
    const revision = taskStatus === TaskStatus.MORE_INFO_NEEDED;
    return (
      <div className={classes.review} ref={available.ref} style={heightStyle}>
        <div className={classes.progress}>
          <div className={classes.progressText}>
            <div className={classes.context}>
              <span className={classes.loan}>Loan {loanId}</span>
              {taskStatus && (
                <span className={clsx(classes.statusChip, revision && classes.statusChipRevision)}>
                  <span className={clsx(classes.statusDot, revision && classes.statusDotRevision)} />
                  {taskStatus}
                </span>
              )}
              <span aria-hidden="true">·</span>
              <span>{store.sections.length} documents · {counts.total} fields</span>
              <span aria-hidden="true">·</span>
              <span className={classes.legendItem}>
                <span className={classes.swatch} style={{ backgroundColor: qcPalette.flagged }} />
                {counts.flagged} flagged
              </span>
            </div>
          </div>
          <ToggleButtonGroup
            exclusive
            aria-label="Filter fields"
            className={classes.filters}
            value={store.filter}
            onChange={(_, value: QcFilter | null) => value && store.setFilter(value)}
          >
            {FILTERS.map(filter => (
              <ToggleButton key={filter.value} value={filter.value} className={classes.filter}>
                {filter.label}
                <span className={classes.filterCount}>{filterCounts[filter.value]}</span>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </div>
        {accepted && <div className={classes.notice}>This review was completed. It is read-only.</div>}
        <div className={classes.workspace}>
          <div className={classes.fields}>
            <PostQcFieldTable store={store} readOnly={readOnly} keyboardEnabled={!confirmAction && !completeBlocked} />
          </div>
          <div className={classes.viewer}>
            <PostQcDocumentViewer store={store} />
          </div>
        </div>
      </div>
    );
  };

  const showActions = !readOnly && !store.loading && !store.loadError && store.sections.length > 0;

  const actions = (
    <div className={classes.footer}>
      <span className={classes.saveState}>
        {store.isDirty && !readOnly && <span className={classes.dirtyDot} />}
        {store.isDirty && !readOnly ? 'Unsaved changes' : ''}
        {store.lastSavedAt ? `${store.isDirty && !readOnly ? ' · ' : ''}Last saved ${formatTime(store.lastSavedAt)}` : ''}
      </span>
      <span className={classes.shortcuts}>
        {showActions && SHORTCUTS.map(shortcut => (
          <span key={shortcut.keys} className={classes.shortcut}>
            <kbd className={classes.kbd}>{shortcut.keys}</kbd>
            {shortcut.label}
          </span>
        ))}
      </span>
      <Grid container spacing={1} justifyContent="flex-end" style={{ width: 'auto', flexWrap: 'nowrap' }}>
        <Grid item>
          <Button testId="post-qc-close" variant="outlined" color="primary" onClick={requestClose}>
            Close
          </Button>
        </Grid>
        {showActions && (
          <>
            <Grid item>
              <WithDocumentActions
                document={loanTask}
                component={Button}
                testId="post-qc-save"
                variant="outlined"
                color="primary"
                disabled={!store.isDirty || store.saving}
                onClick={save}
              >
                Save
              </WithDocumentActions>
            </Grid>
            <Grid item>
              <Tooltip title={sendRevisionHint}>
                <span>
                  <WithDocumentActions
                    document={loanTask}
                    component={Button}
                    testId="post-qc-send-revision"
                    variant="outlined"
                    className={classes.revisionButton}
                    disabled={!store.canSendRevision || store.saving}
                    onClick={() => setConfirmAction('SEND_REVISION')}
                  >
                    Send Revision
                    {counts.flagged > 0 && <span className={classes.revisionCount}>{counts.flagged}</span>}
                  </WithDocumentActions>
                </span>
              </Tooltip>
            </Grid>
            <Grid item>
              <WithDocumentActions
                document={loanTask}
                component={Button}
                testId="post-qc-complete"
                variant="contained"
                color="primary"
                disabled={store.saving}
                onClick={() => (counts.flagged > 0 ? setCompleteBlocked(true) : setConfirmAction('COMPLETE'))}
              >
                Mark as Completed
              </WithDocumentActions>
            </Grid>
          </>
        )}
      </Grid>
    </div>
  );

  if (!taskId) {
    return null;
  }

  return (
    <>
      <CustomTaskDialog
        open={open}
        title={DocumentName.POST_CLOSING_REVIEW}
        taskId={taskId}
        loanChatRoom={{ loanId: Number(loanId), chatRoomType: ChatRoomType.CLOSING, label: 'Closing' }}
        maxWidth={false}
        fullWidth
        fullScreen={false}
        PaperProps={{ style: FULL_VIEWPORT_PAPER }}
        allowVertScroll={false}
        handleClose={requestClose}
        dialogContent={<Grid item xs={12} className={classes.reviewItem}>{renderReview()}</Grid>}
        dialogActions={actions}
      />
      <ConfirmDialog
        open={!!confirmAction}
        title={
          confirmAction === 'CLOSE'
            ? 'Unsaved changes'
            : confirmAction === 'SEND_REVISION'
              ? 'Send Revision'
              : 'Mark as Completed'
        }
        body={confirmBody()}
        confirmButtonText={confirmAction === 'CLOSE' ? 'Close without saving' : 'Confirm'}
        cancelButtonText="Cancel"
        handleClose={() => setConfirmAction(null)}
        handleCancel={() => setConfirmAction(null)}
        handleConfirm={confirm}
      />
      <InformationDialog
        open={completeBlocked}
        title="Can't mark as completed"
        body={
          <Typography>
            {`This review has ${counts.flagged} flagged ${counts.flagged === 1 ? 'field' : 'fields'}. Send the revision to the closing team, or unflag the fields that no longer need changes, before marking the task as completed.`}
          </Typography>
        }
        closeButtonText="Got it"
        handleClose={() => setCompleteBlocked(false)}
      />
    </>
  );
});
